import { useCallback, useMemo } from 'react';
import { usePrivy, useWallets, useFundWallet as usePrivyFundWallet } from '@privy-io/react-auth';
import {
  useAccount,
  useBalance,
  useChainId,
  useSwitchChain,
  useSendTransaction,
  useWaitForTransactionReceipt,
} from 'wagmi';
import { parseEther, formatEther, type Address } from 'viem';
import { base, baseSepolia } from 'wagmi/chains';

// USDC contract addresses by chain
const USDC_ADDRESSES: Record<number, Address> = {
  [base.id]: '0x833589fCD6eDb6E08f4c7C32D4f71b54bdA02913', // Base USDC
  [baseSepolia.id]: '0x036CbD53842c5426634e7929541eC2318f3dCF7e', // Base Sepolia USDC
};

interface UseWalletReturn {
  // Wallet state — always the Privy embedded wallet address
  address: Address | undefined;
  isConnected: boolean;
  hasEmbeddedWallet: boolean;

  // Chain state
  chainId: number | undefined;
  isCorrectChain: boolean;
  switchChain: (chainId: number) => void;
  isSwitchingChain: boolean;

  // Balance
  ethBalance: string;
  isLoadingBalance: boolean;

  // Transactions
  sendTransaction: (params: SendTransactionParams) => Promise<`0x${string}`>;
  isSending: boolean;
  waitForTransaction: (hash: `0x${string}`) => Promise<void>;

  // Privy wallet actions
  exportWallet: () => void;
  fundWallet: () => void;
}

interface SendTransactionParams {
  to: Address;
  value?: string; // ETH amount in ether
  data?: `0x${string}`;
}

const SUPPORTED_CHAIN_IDS: number[] = [base.id, baseSepolia.id];

export function useWallet(): UseWalletReturn {
  const { exportWallet } = usePrivy();
  const { wallets } = useWallets();

  // Wagmi hooks
  const { address, isConnected } = useAccount();
  const chainId = useChainId();
  const { switchChain: wagmiSwitchChain, isPending: isSwitchingChain } = useSwitchChain();

  const { data: balanceData, isLoading: isLoadingBalance } = useBalance({
    address,
    query: {
      enabled: !!address,
    },
  });

  const {
    sendTransactionAsync,
    isPending: isSending,
    data: txHash,
  } = useSendTransaction();

  const { isLoading: isConfirming, isSuccess: isConfirmed } = useWaitForTransactionReceipt({
    hash: txHash,
  });

  // Find the Privy embedded wallet — this is the only wallet we use
  const embeddedWallet = useMemo(() => {
    return wallets.find((w) => w.walletClientType === 'privy');
  }, [wallets]);

  const hasEmbeddedWallet = !!embeddedWallet;

  // Check if on supported chain
  const isCorrectChain = useMemo(() => {
    return chainId !== undefined && SUPPORTED_CHAIN_IDS.includes(chainId);
  }, [chainId]);

  // Switch chain
  const switchChain = useCallback(
    (targetChainId: number) => {
      if (!SUPPORTED_CHAIN_IDS.includes(targetChainId)) {
        console.error('[Wallet] Unsupported chain:', targetChainId);
        return;
      }
      wagmiSwitchChain({ chainId: targetChainId as typeof base.id | typeof baseSepolia.id });
    },
    [wagmiSwitchChain]
  );

  // Send transaction
  const sendTransaction = useCallback(
    async (params: SendTransactionParams): Promise<`0x${string}`> => {
      if (!address) {
        throw new Error('Wallet not connected');
      }

      if (!isCorrectChain) {
        throw new Error('Please switch to a supported network');
      }

      const hash = await sendTransactionAsync({
        to: params.to,
        value: params.value ? parseEther(params.value) : undefined,
        data: params.data,
      });

      return hash;
    },
    [address, isCorrectChain, sendTransactionAsync]
  );

  // Wait for transaction confirmation
  const waitForTransaction = useCallback(
    async (_hash: `0x${string}`): Promise<void> => {
      // This would use useWaitForTransactionReceipt in a real implementation
      // For now, we'll just return a promise that resolves
      // The actual waiting is handled by the hook
      return new Promise((resolve) => {
        // Poll for confirmation - in practice, use the hook's state
        const interval = setInterval(() => {
          if (isConfirmed) {
            clearInterval(interval);
            resolve();
          }
        }, 1000);

        // Timeout after 2 minutes
        setTimeout(() => {
          clearInterval(interval);
          resolve();
        }, 120000);
      });
    },
    [isConfirmed]
  );

  // Fund wallet (opens Privy funding flow)
  const { fundWallet: privyFundWallet } = usePrivyFundWallet();
  const fundWallet = useCallback(() => {
    if (!address) return;
    privyFundWallet(address);
  }, [address, privyFundWallet]);

  // Format ETH balance
  const ethBalance = useMemo(() => {
    if (!balanceData) return '0';
    return formatEther(balanceData.value);
  }, [balanceData]);

  return {
    address,
    isConnected,
    hasEmbeddedWallet,
    chainId,
    isCorrectChain,
    switchChain,
    isSwitchingChain,
    ethBalance,
    isLoadingBalance,
    sendTransaction,
    isSending: isSending || isConfirming,
    waitForTransaction,
    exportWallet,
    fundWallet,
  };
}

/**
 * Hook to get USDC contract address for current chain
 */
export function useUSDCAddress(): Address | undefined {
  const chainId = useChainId();
  return chainId ? USDC_ADDRESSES[chainId] : undefined;
}

/**
 * Hook to check if wallet needs funding
 */
export function useNeedsFunding(requiredAmount: string): boolean {
  const { ethBalance, isLoadingBalance } = useWallet();

  if (isLoadingBalance) return false;

  const required = parseFloat(requiredAmount);
  const available = parseFloat(ethBalance);

  return available < required;
}
